import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  tracing_sampleRate_global: z.coerce
    .number({
      required_error: 'Required',
      invalid_type_error: 'Must be a number',
    })
    .min(0, { message: 'Must be >= 0' })
    .max(1, { message: 'Must be <= 1' }),
  tracing_sampleRate_local: z.coerce
    .number({
      required_error: 'Required',
      invalid_type_error: 'Must be a number',
    })
    .min(0, { message: 'Must be >= 0' })
    .max(1, { message: 'Must be <= 1' }),
});

export const TracingSampleRates = () => {
  const [updateConfig, { isLoading: isSaving }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const {
    remoteAppConfig: {
      tracing_sampleRate_global,
      tracing_sampleRate_local,
    },
  } = useGetAppConfig();

  const { onSettled } = useOnSettled();

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
    getValues,
  } = useForm({
    resolver: zodResolver(schema as any),
    defaultValues: {
      tracing_sampleRate_global: tracing_sampleRate_global ?? 0.1,
      tracing_sampleRate_local: tracing_sampleRate_local ?? 1,
    },
  });

  useEffect(() => {
    setValue(
      'tracing_sampleRate_global',
      tracing_sampleRate_global ?? 0.1,
    );
    setValue(
      'tracing_sampleRate_local',
      tracing_sampleRate_local ?? 1,
    );
  }, [tracing_sampleRate_global, tracing_sampleRate_local, setValue]);

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  return (
    <Box marginTop="2rem">
      <h2>Tracing Sample Rates</h2>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="1rem"
        >
          <TextField
            label="Global Sample Rate"
            helperText="Global tracing sample rate (0.0 to 1.0)"
            disabled={isSaving}
            error={!!formState.errors.tracing_sampleRate_global}
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('tracing_sampleRate_global', {
              required: true,
            })}
          />
          <TextField
            label="Local Sample Rate"
            helperText="Local tracing sample rate (0.0 to 1.0)"
            disabled={isSaving}
            error={!!formState.errors.tracing_sampleRate_local}
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('tracing_sampleRate_local', {
              required: true,
            })}
          />
          {(formState.isDirty || formState.isSubmitting) && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving || !formState.isValid}
              endIcon={isSaving && <CircularProgress size={14} />}
            >
              {isSaving ? 'Updating' : 'Update'} Tracing Sample Rates
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};
